<template>
    <div>
        <loader v-if="loading" />
        <template v-if="!loading" >
            <br/>
            <statistical-member-or-senator-selector :value="selectedMpOrSenator" @input="selectedMpOrSenator = $event" :multiple="false" :alternateText="true" />
            <br/>
            <chart v-if="!loadingAllDays" :chartData="chartData" />
            <br/>
            <h3>Dates of the year</h3>
            <v-container grid-list-xl>
                <v-layout row wrap>
                    <v-flex v-for="month in calendar" :key="month.name" lg2 md3 sm4 xs12>
                        <div class="sitting-day-calendar__month" :class="{'sitting-day-calendar__month--reps': selectedMpOrSenator === 'mp', 'sitting-day-calendar__month--senate': selectedMpOrSenator === 'senator'}">
                            <h4>{{ month.name }}</h4></div>
                        <table class="sitting-day-calendar">
                            <tbody>
                                <tr v-for="(week, index) in month.rows" :key="index">
                                    <v-tooltip top v-for="day in week" :key="day.date.toString()">
                                        <template v-slot:activator="{ on, attrs }">
                                            <td
                                            :class="getHeat(selectedMpOrSenator === 'mp' ? day.repsSitting : day.senateSitting, selectedMpOrSenator === 'mp' ? 'RepsFrequency' : 'SenateFrequency')"
                                            v-bind="attrs"
                                            v-on="on"
                                            >
                                                <span>{{ day.date.date() }}</span>
                                            </td>
                                        </template>
                                        <div>{{ day.date.date() }}/{{ (day.date.month() + 1) }}</div>
                                        <div>{{ selectedMpOrSenator === 'mp' ?  day.repsSitting : day.senateSitting }} sitting days</div>
                                    </v-tooltip>
                                </tr>
                            </tbody>
                        </table>
                    </v-flex>
                </v-layout>
            </v-container>
            <h4>Legend</h4>
            <div class="calendar-legend">
                <span>0</span><div style="width: 20px; height: 20px; float: left; background: white"></div>
                <div v-for="l in legend" :key="l.low">
                    <span>{{ l.low }} - {{ l.high }}</span><div style="width: 20px; height: 20px; float: left;" :class="getHeatClass(l.heat, selectedMpOrSenator === 'mp' ? 'RepsFrequency': 'SenateFrequency')"></div>
                </div>
            </div>
            <br/>
            <p>This chart depicts the historical frequency of sitting days on particular dates</p>
        </template>
    </div>
</template>
<script>
import Loader from '@/components/Loader.vue';
import axios from 'axios';
import moment from 'moment'
import Chart from './Chart.vue';
import StatisticalMemberOrSenatorSelector from './StatisticalMemberOrSenatorSelector.vue';
import groupBy from 'lodash.groupby';

function percentile(numbers, p) {
  numbers.sort((a, b) => a - b);  // sort the numbers in ascending order
  let index = p * (numbers.length - 1);
  if (Math.floor(index) === index) {
    return numbers[index];
  } else {
    let lower = numbers[Math.floor(index)];
    let upper = numbers[Math.ceil(index)];
    return (lower + upper) / 2;
  }
}

function getAllDaysInMonth(year, month) {
  const date = new Date(year, month, 1);

  const dates = [];

  while (date.getMonth() === month) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }

  return dates;
}

export default {
    created() {
        this.initialiseItems();
        this.initialiseAllDays();
    },
    components: {
        Loader,
        StatisticalMemberOrSenatorSelector,
        Chart
    },
    props: ['year'],
    data() {
        return {
            items: [],
            itemsAllDays: [],
            loading: true,
            loadingAllDays: true,
            csvDownloading: false,
            selectedMpOrSenator: 'mp'
        }
    },
    methods: {
        initialiseItems() {
            this.loading = true;
            const url = `StatisticalInformation/SittingDayFrequency`;
            axios.get(`api/${url}`)
                .then(response => {
                    this.items = response.data;
                    this.loading = false;
                });
        },
        initialiseAllDays() {
            this.loadingAllDays = true;
            const url = `StatisticalInformation/SittingDaysForYear?year=`;
            axios.get(`api/${url}`)
                .then(response => {
                    this.itemsAllDays = response.data;
                    this.loadingAllDays = false;
                });
        },
        getHeat(frequency, frequencyType) {
            const position = this.getPositionOutOf10(frequency, frequencyType);
            return this.getHeatClass(position, frequencyType);
        },
        getHeatClass(position, frequencyType) {
            return 'heat-' + position + '-' + (frequencyType === 'RepsFrequency' ? 'reps' : 'senate' );
        },
        getPositionOutOf10(frequency, frequencyType) {
            if (frequency === 0) return 0;
            const sortedFrequencies = [...this.items].map(i => i[frequencyType]).sort((a, b) => a - b)
            const index = sortedFrequencies.findIndex(f => f === frequency) + 1;
            return Math.ceil(index / this.items.length * 10);
        },
        exportCsv: function () {
            this.csvDownloading = true;
            const records = this.items.map(i => 
                ({ 
                    Date: (new Date(i.Date)).toLocaleDateString(),
                    'House sitting': i.RepsSitting,
                    'Senate sitting': i.SenateSitting
                }));
            let headers = ['Date', 'House sitting', 'Senate sitting'];
            const filename = 'Sitting days for year';
            const comment = `Data current as of ${(new Date()).toLocaleDateString()}`;
            this.$store.dispatch('GETCSV', { DATA: records, HEADERS: headers, FILENAME: filename, COMMENT: comment })
                .catch(this.showError)
                .finally(() => this.csvDownloading = false);
        }
    },
    computed: {
        csvDownloadIcon() {
            return this.csvDownloading ? 'cached' : 'text_snippet';
        },
        csvDownloadClass() {
            let csvClass = 'mr-1';
            if (this.csvDownloading){
                csvClass = csvClass + ' loading'
            }
            return csvClass;
        },
        title() {
            return `Sitting days for ${this.year}`
        },
        showBack() {
            return this.year > 1901;
        },
        showForward() {
            return this.year < new Date().getFullYear();
        },
        chartData() {
            const groupedData = groupBy(this
                .itemsAllDays
                .filter(i => this.selectedMpOrSenator === 'mp' ? i.RepsSitting : i.SenateSitting),
                d => moment(d.Date).isoWeekday());

            let data = []

            for (const key of Object.keys(groupedData)) {
                // do something with object[key]
                data.push(groupedData[key].length)
            }

            return {
                chart: {
                    type: 'bar'
                },
                title: {
                    text: 'Days of the week'
                },
                xAxis: {
                    categories: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
                },
                yAxis: {
                    title: {
                        text: 'Occurences'
                    }
                },
                plotOptions: {
                    bar: {
                        dataLabels: {
                            enabled: true
                        }
                    }
                },
                exporting: {
                    filename: 'Sitting days by days of the week'
                },
                series: [{
                    name: '',
                    data
                }],
                legend: {
                    enabled: false
                }
            }
        },
        calendar() {
            // get a full calendar of days. 
            const calendar = [];
            for(let i = 0;i <= 11;i++) {
                // get rows
                // start from 1st of the month, 
                // keep going until last day of the week of the last day of the month is reached.
                const year = new Date(2020, 1, 1).getFullYear();
                const daysInMonth = getAllDaysInMonth(year, i);
                const firstDay = moment(daysInMonth[0]).startOf('month');
                const lastDay = moment(daysInMonth[daysInMonth.length - 1]).endOf('month');
                const month = new Date(year, i, 1);
                const monthName = month.toLocaleString('default', { month: 'long' });

                let j = firstDay;
                let rows = [];
                let row = [];
                let k = 1;
                while(j <= lastDay) {
                    let repsFrequency = 0;
                    let senateFrequency = 0;
                    const sittingDay = this.items.find(i => i.Day === j.date() && i.Month - 1 === j.month());
                    if (sittingDay) {
                        repsFrequency = sittingDay.RepsFrequency;
                        senateFrequency = sittingDay.SenateFrequency;
                    }
                    row.push({
                        date: moment(j),
                        repsSitting: repsFrequency,
                        senateSitting: senateFrequency 
                    })
                    if ((k !== 0 && k % 7 === 0) || j.isSame(lastDay, 'day')) {
                        rows.push(row);
                        row = [];
                    }
                    j.add(1, 'day');
                    k++;
                }

                calendar.push({ name: monthName, rows });
            }
            return calendar
        },
        legend() {
            const frequencyType = this.selectedMpOrSenator === 'mp' ? 'RepsFrequency': 'SenateFrequency';
            const percentiles = [
                { low: 0, high: 0.1, heat: 1 }, 
                { low: 0.1, high: 0.2, heat: 2 },
                { low: 0.2, high: 0.3, heat: 3 },
                { low: 0.3, high: 0.4, heat: 4 },
                { low: 0.4, high: 0.5, heat: 5 },
                { low: 0.5, high: 0.6, heat: 6 },
                { low: 0.6, high: 0.7, heat: 7 },
                { low: 0.7, high: 0.8, heat: 8 },
                { low: 0.8, high: 0.9, heat: 9 },
                { low: 0.9, high: 1.0, heat: 10 },
            ];
            return percentiles.map(p => 
            { 
                return ({
                    low: p.low === 0 ? 1 : percentile(this.items.map(i => i[frequencyType]), p.low) + 1,
                    high: percentile(this.items.map(i => i[frequencyType]), p.high),
                    heat: p.heat,
                    repsColour: p.repsColour,
                    senateColour: p.senateColour
                });
            })
        }
    }
}
</script>
<style scoped>
    .sitting-day-calendar {
        text-align: center;
        width: 90%;
        margin: auto;
        background: #ddd;        
        border-collapse: collapse;
    }
    .sitting-day-calendar td {
        height: 34px;
    }
    .house-sitting {
        background-color: #b1f9b1;
    }
    .senate-sitting {
       background-color: #ffd7d7; 
    }
    .both-sitting {
        background-color: #a7ddff;
    }
    .sitting-day-calendar__month {
        text-align: center;
        background-color: #325986;
        width: 90%;
        margin: auto;
        padding: .75rem 0 .75rem;
        color: white;
    }
    .sitting-day-calendar__month--reps {
        background-color: #1B5E20;
    }
    .sitting-day-calendar__month--senate {
        background-color: #B71C1C;
    }
    .sitting-day-calendar thead {
        background: #f5f5f5;
    }
    .grid-list-xl {
        padding-left: 0;
        padding-right: 0;
    }
    .calendar-legend > div {
        margin: 10px;
        display: inline-block;
    }
    .calendar-legend span {
        margin-left: 5px;
    }
    .v-icon {
        vertical-align: middle;
    }
    .heat-0-reps {
        background-color: white;
    }
    .heat-1-reps {
        background-color: #E8F5E9;
    }
    .heat-2-reps {
        background-color: #C8E6C9;
    }
    .heat-3-reps {
        background-color: #A5D6A7;
    }
    .heat-4-reps {
        background-color: #81C784;
    }
    .heat-5-reps {
        background-color: #66BB6A;
    }
    .heat-6-reps {
        background-color: #4CAF50;
    }
    .heat-7-reps {
        background-color: #43A047;
    }
    .heat-8-reps {
        background-color: #388E3C;
    }
    .heat-9-reps {
        background-color: #2E7D32;
        color: white;
    }
    .heat-10-reps {
        background-color: #1B5E20;
        color: white;
    }
    .heat-0-senate {
        background-color: white;
    }
    .heat-1-senate {
        background-color: #FFEBEE;
    }
    .heat-2-senate {
        background-color: #FFCDD2;
    }
    .heat-3-senate {
        background-color: #EF9A9A;
    }
    .heat-4-senate {
        background-color: #E57373;
    }
    .heat-5-senate {
        background-color: #EF5350;
    }
    .heat-6-senate {
        background-color: #F44336;
    }
    .heat-7-senate {
        background-color: #E53935;
    }
    .heat-8-senate {
        background-color: #D32F2F;
    }
    .heat-9-senate {
        background-color: #C62828;
        color: white;
    }
    .heat-10-senate {
        background-color: #B71C1C;
        color: white;
    }
</style>
